import { useState, useRef, KeyboardEvent, useEffect } from 'react';
import { 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Alert
} from '@mui/material';
import { Agent } from '../../types/business';

interface AddAgentFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (agentData: {
    userEmail: string;
    agentName: string;
    agentEmail?: string;
    agentPhone?: string;
    agentProfile?: string;
    agentProfileUrl?: string;
    agentProfileImage?: string;
    isActive: boolean;
  }) => Promise<{ success: boolean; error?: string }>;
  onUpdate?: (
    userEmail: string,
    updates: {
      agentName?: string;
      agentEmail?: string;
      agentPhone?: string;
      agentProfile?: string;
      agentProfileUrl?: string;
      agentProfileImage?: string;
      isActive?: boolean;
    }
  ) => Promise<{ success: boolean; error?: string }>;
  isSubmitting: boolean;
  isEdit?: boolean;
  editAgent?: Agent;
}

const AddAgentForm: React.FC<AddAgentFormProps> = ({
  open,
  onClose,
  onSubmit,
  onUpdate,
  isSubmitting,
  isEdit = false,
  editAgent
}) => {
  const [userEmail, setUserEmail] = useState('');
  const [agentName, setAgentName] = useState('');
  const [agentEmail, setAgentEmail] = useState('');
  const [agentPhone, setAgentPhone] = useState('');
  const [agentProfile, setAgentProfile] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const userEmailRef = useRef<HTMLInputElement>(null);
  
  // Initialize form with edit data when in edit mode
  useEffect(() => {
    if (open && isEdit && editAgent) {
      setUserEmail(editAgent.user.email);
      setAgentName(editAgent.name);
      setAgentEmail(editAgent.email === editAgent.user.email ? '' : editAgent.email || '');
      setAgentPhone(editAgent.phone || '');
      setAgentProfile(editAgent.profile || '');
      setIsActive(editAgent.isActive);
    } else if (open && !isEdit) {
      // Reset form for add mode
      setUserEmail('');
      setAgentName('');
      setAgentEmail('');
      setAgentPhone('');
      setAgentProfile('');
      setIsActive(true);
    }
    setError(null);
  }, [open, isEdit, editAgent]);
  
  const handleSubmit = async () => {
    if (!userEmail.trim() || !agentName.trim()) {
      setError('User email and agent name are required');
      return;
    }
    
    setError(null);
    
    let result;
    if (isEdit && onUpdate) {
      // For edit mode, only send changed fields
      const updates: any = {};
      
      if (agentName.trim() !== editAgent?.name) {
        updates.agentName = agentName.trim();
      }
      
      const currentEmail = editAgent?.email === editAgent?.user.email ? '' : editAgent?.email || '';
      if (agentEmail.trim() !== currentEmail) {
        updates.agentEmail = agentEmail.trim() || undefined;
      }
      
      if (agentPhone.trim() !== (editAgent?.phone || '')) {
        updates.agentPhone = agentPhone.trim() || undefined;
      }
      
      if (agentProfile.trim() !== (editAgent?.profile || '')) {
        updates.agentProfile = agentProfile.trim() || undefined;
      }
      
      if (isActive !== editAgent?.isActive) {
        updates.isActive = isActive;
      }
      
      result = await onUpdate(userEmail.trim(), updates);
    } else {
      // For add mode, send all data
      result = await onSubmit({
        userEmail: userEmail.trim(),
        agentName: agentName.trim(),
        agentEmail: agentEmail.trim() || undefined,
        agentPhone: agentPhone.trim() || undefined,
        agentProfile: agentProfile.trim() || undefined,
        isActive
      });
    }

    if (result.success) {
      handleClose();
    } else {
      setError(result.error || `Failed to ${isEdit ? 'update' : 'add'} agent`);
    }
  };
  
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !isSubmitting && userEmail.trim() && agentName.trim()) {
      handleSubmit();
    }
  };
  
  const handleClose = () => {
    setUserEmail('');
    setAgentName('');
    setAgentEmail('');
    setAgentPhone('');
    setAgentProfile('');
    setIsActive(true);
    setError(null);
    onClose();
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={{ zIndex: 3001 }}
      slotProps={{
        transition: {
          onEntered: () => {
            if (!isEdit) {
              userEmailRef.current?.focus();
            }
          }
        }
      }}
    >
      <DialogTitle>{isEdit ? 'Edit Agent' : 'Add New Agent'}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          )}
          
          <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
            Agent Information
          </Typography>
          
          <TextField
            inputRef={userEmailRef}
            autoFocus={!isEdit}
            label="User Email *"
            type="email"
            fullWidth
            variant="outlined"
            value={userEmail}
            disabled={isSubmitting || isEdit} // Disable email editing in edit mode
            onChange={(e) => setUserEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            required
            helperText={isEdit ? "Email cannot be changed" : "Email of the existing user to add as an agent"}
            error={!!error && !userEmail.trim()}
            margin="dense"
            InputLabelProps={{
              style: { 
                zIndex: 1,
                background: 'transparent'
              }
            }}
            sx={{ 
              '& .MuiInputBase-root': { transform: 'translate(0px, 5px)' },
              '& .MuiFormHelperText-root': { transform: 'translate(0px, 5px)' },
            }}
          />
          
          <TextField
            autoFocus={isEdit}
            label="Agent Name *"
            type="text"
            fullWidth
            variant="outlined"
            value={agentName}
            disabled={isSubmitting}
            onChange={(e) => setAgentName(e.target.value)}
            onKeyDown={handleKeyDown}
            required
            helperText="Display name for this agent"
            error={!!error && !agentName.trim()}
            margin="dense"
            InputLabelProps={{
              style: { 
                zIndex: 1,
                background: 'transparent'
              }
            }}
            sx={{ 
              '& .MuiInputBase-root': { transform: 'translate(0px, 5px)' },
              '& .MuiFormHelperText-root': { transform: 'translate(0px, 5px)' },
            }}
          />
          
          <TextField
            label="Agent Email"
            type="email"
            fullWidth
            variant="outlined"
            value={agentEmail}
            disabled={isSubmitting}
            onChange={(e) => setAgentEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            helperText="Business email for this agent (optional, leave blank to use user email)"
            margin="dense"
            InputLabelProps={{
              style: { 
                zIndex: 1,
                background: 'transparent'
              }
            }}
            sx={{ 
              '& .MuiInputBase-root': { transform: 'translate(0px, 5px)' },
              '& .MuiFormHelperText-root': { transform: 'translate(0px, 5px)' },
            }}
          />
          
          <TextField
            label="Agent Phone"
            type="tel"
            fullWidth
            variant="outlined"
            value={agentPhone}
            disabled={isSubmitting}
            onChange={(e) => setAgentPhone(e.target.value)}
            onKeyDown={handleKeyDown}
            helperText="Business phone number for this agent (optional)"
            margin="dense"
            InputLabelProps={{
              style: { 
                zIndex: 1,
                background: 'transparent'
              }
            }}
            sx={{ 
              '& .MuiInputBase-root': { transform: 'translate(0px, 5px)' },
              '& .MuiFormHelperText-root': { transform: 'translate(0px, 5px)' },
            }}
          />
          
          <TextField
            label="Agent Profile"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={agentProfile}
            disabled={isSubmitting}
            onChange={(e) => setAgentProfile(e.target.value)}
            helperText="Brief description or bio for this agent (optional)"
            margin="dense"
            InputLabelProps={{
              style: { 
                zIndex: 1,
                background: 'transparent'
              }
            }}
            sx={{ 
              '& .MuiInputBase-root': { transform: 'translate(0px, 5px)' },
              '& .MuiFormHelperText-root': { transform: 'translate(0px, 5px)' },
            }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={isSubmitting}
                color="primary"
              />
            }
            label="Active Agent"
            sx={{ mt: 1 }}
          />
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            * Required fields
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={!userEmail.trim() || !agentName.trim() || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
        >
          {isSubmitting 
            ? `${isEdit ? 'Updating' : 'Adding'} Agent...` 
            : `${isEdit ? 'Update' : 'Add'} Agent`
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAgentForm;